/**
 * Sophia Platform (Cloudflare Edge-Native Edition)
 *
 * @file        Brand Identity & Corporate Profile Studio View (Task 11.5.4)
 * @author      Le Viet Quang <quangle@fourelementscorp.com>
 * @copyright   (c) 2026 Le Viet Quang / Four Elements Corp
 * @license     MIT
 */

import React, { useState, useEffect, useRef } from 'react';
import {
  Sparkles,
  Upload,
  Globe,
  Building,
  Share2,
  Eye,
  RefreshCw,
  Save,
  CheckCircle2,
  AlertCircle,
  X,
  Mail,
  Phone,
  MapPin,
  Facebook,
  Twitter,
  Linkedin,
  Github,
  Youtube,
  ImageIcon,
} from 'lucide-react';
import { useI18n } from '../i18n';
import { useTheme } from '../context/ThemeProvider';
import { getAdminDomains } from '../lib/urls';
import { getAdminToken } from '../lib/api-client';
import type { BrandSettings, UpdateBrandSettingsInput } from '@sofia/shared-types';

export const BrandStudioView: React.FC = () => {
  const { t } = useI18n();
  const { refreshBranding } = useTheme();
  const domains = getAdminDomains();

  const [settings, setSettings] = useState<BrandSettings | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadingAsset, setUploadingAsset] = useState<string | null>(null);
  const [toastMsg, setToastMsg] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  // Hidden file input references
  const fileInputRefs = {
    'logo-light': useRef<HTMLInputElement>(null),
    'logo-dark': useRef<HTMLInputElement>(null),
    'logo-collapsed': useRef<HTMLInputElement>(null),
    favicon: useRef<HTMLInputElement>(null),
    'og-image': useRef<HTMLInputElement>(null),
  };

  const fetchBrandSettings = async () => {
    try {
      setLoading(true);
      const token = getAdminToken() || '';
      const res = await fetch(`${domains.apiOrigin}/api/v1/admin/brand-settings`, {
        headers: {
          Authorization: token ? `Bearer ${token}` : '',
        },
      });

      if (!res.ok) {
        throw new Error(`HTTP ${res.status}`);
      }

      const data = (await res.json()) as { success: boolean; data: BrandSettings };
      if (data.success && data.data) {
        setSettings(data.data);
      }
    } catch (err: any) {
      setToastMsg({
        type: 'error',
        text: err?.message || 'Failed to load brand settings',
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBrandSettings();
  }, []);

  const handleSave = async (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    if (!settings) return;

    try {
      setSaving(true);
      const token = getAdminToken() || '';
      const payload: UpdateBrandSettingsInput = {
        siteName: settings.siteName,
        siteDescription: settings.siteDescription,
        slogan: settings.slogan,
        logoLightUrl: settings.logoLightUrl,
        logoDarkUrl: settings.logoDarkUrl,
        logoCollapsedUrl: settings.logoCollapsedUrl,
        faviconUrl: settings.faviconUrl,
        ogImageUrl: settings.ogImageUrl,
        companyName: settings.companyName,
        address: settings.address,
        contactEmail: settings.contactEmail,
        hotline: settings.hotline,
        taxId: settings.taxId,
        copyrightText: settings.copyrightText,
        socialLinks: settings.socialLinks,
      };

      const res = await fetch(`${domains.apiOrigin}/api/v1/admin/brand-settings`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: token ? `Bearer ${token}` : '',
        },
        body: JSON.stringify(payload),
      });

      const result = (await res.json()) as { success: boolean; data?: BrandSettings; error?: string };
      if (!res.ok || !result.success || !result.data) {
        throw new Error(result.error || `HTTP ${res.status}`);
      }

      setSettings(result.data);
      await refreshBranding();
      setToastMsg({
        type: 'success',
        text: t('admin.brandStudio.toast.saveSuccess'),
      });
    } catch (err: any) {
      setToastMsg({
        type: 'error',
        text: t('admin.brandStudio.toast.saveError', { error: err?.message || 'Unknown error' }),
      });
    } finally {
      setSaving(false);
    }
  };

  const handleFileUpload = async (assetType: string, file: File) => {
    try {
      setUploadingAsset(assetType);
      const token = getAdminToken() || '';
      const formData = new FormData();
      formData.append('file', file);
      formData.append('assetType', assetType);

      const res = await fetch(`${domains.apiOrigin}/api/v1/admin/brand-settings/upload`, {
        method: 'POST',
        headers: {
          Authorization: token ? `Bearer ${token}` : '',
        },
        body: formData,
      });

      const result = (await res.json()) as {
        success: boolean;
        data?: { url: string; key: string };
        error?: string;
      };

      if (!res.ok || !result.success || !result.data) {
        throw new Error(result.error || `HTTP ${res.status}`);
      }

      const uploadedUrl = result.data.url;

      // Update state field according to assetType
      if (settings) {
        let fieldName: keyof BrandSettings | null = null;
        if (assetType === 'logo-light') fieldName = 'logoLightUrl';
        else if (assetType === 'logo-dark') fieldName = 'logoDarkUrl';
        else if (assetType === 'logo-collapsed') fieldName = 'logoCollapsedUrl';
        else if (assetType === 'favicon') fieldName = 'faviconUrl';
        else if (assetType === 'og-image') fieldName = 'ogImageUrl';

        if (fieldName) {
          setSettings({
            ...settings,
            [fieldName]: uploadedUrl,
          });
        }
      }

      setToastMsg({
        type: 'success',
        text: t('admin.brandStudio.toast.uploadSuccess'),
      });
    } catch (err: any) {
      setToastMsg({
        type: 'error',
        text: t('admin.brandStudio.toast.uploadError', { error: err?.message || 'Upload failed' }),
      });
    } finally {
      setUploadingAsset(null);
    }
  };

  const updateField = (field: keyof BrandSettings, value: any) => {
    if (!settings) return;
    setSettings({
      ...settings,
      [field]: value,
    });
  };

  const updateSocialLink = (network: string, value: string) => {
    if (!settings) return;
    setSettings({
      ...settings,
      socialLinks: {
        ...settings.socialLinks,
        [network]: value,
      },
    });
  };

  return (
    <div className="space-y-6">
      {/* Toast Notification Banner */}
      {toastMsg && (
        <div
          className={`p-4 rounded-xl flex items-center justify-between text-xs font-medium border ${
            toastMsg.type === 'success'
              ? 'bg-emerald-500/10 border-emerald-500/20 text-emerald-400'
              : 'bg-rose-500/10 border-rose-500/20 text-rose-400'
          }`}
        >
          <div className="flex items-center gap-2">
            {toastMsg.type === 'success' ? (
              <CheckCircle2 className="w-4 h-4 shrink-0" />
            ) : (
              <AlertCircle className="w-4 h-4 shrink-0" />
            )}
            <span>{toastMsg.text}</span>
          </div>
          <button
            onClick={() => setToastMsg(null)}
            className="hover:opacity-75 transition-opacity p-1"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Header Toolbar */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <div className="flex items-center gap-2">
            <Sparkles className="w-6 h-6 text-primary" />
            <h1 className="text-xl font-heading font-bold text-foreground">
              {t('admin.brandStudio.title')}
            </h1>
          </div>
          <p className="text-xs text-muted-fg mt-1">
            {t('admin.brandStudio.subtitle')}
          </p>
        </div>

        <div className="flex items-center gap-3">
          <button
            onClick={fetchBrandSettings}
            disabled={loading}
            className="px-3.5 py-2 text-xs font-medium text-foreground bg-surface border border-surface-border rounded-xl hover:bg-surface-elevated transition-colors flex items-center gap-2 disabled:opacity-50"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
            {t('common.refresh')}
          </button>
          <button
            onClick={() => handleSave()}
            disabled={saving || loading || !settings}
            className="px-4 py-2 text-xs font-semibold text-white bg-primary hover:bg-primary-hover rounded-xl shadow-glow-primary transition-all flex items-center gap-2 disabled:opacity-50"
          >
            <Save className="w-4 h-4" />
            {saving ? t('admin.brandStudio.actions.saving') : t('admin.brandStudio.actions.save')}
          </button>
        </div>
      </div>

      {settings && (
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          {/* Main Configuration Forms (7 cols) */}
          <div className="lg:col-span-7 space-y-6">
            {/* 1. Visual Brand Assets */}
            <div className="bg-surface border border-surface-border rounded-2xl p-6 space-y-5">
              <div className="flex items-center gap-2 pb-3 border-b border-surface-border">
                <ImageIcon className="w-4 h-4 text-primary" />
                <h2 className="text-sm font-heading font-bold text-foreground">
                  {t('admin.brandStudio.sections.visualAssets')}
                </h2>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {/* Logo Light */}
                <div className="p-4 bg-surface-elevated border border-surface-border rounded-xl space-y-3 flex flex-col justify-between">
                  <div>
                    <h3 className="text-xs font-bold text-foreground">
                      {t('admin.brandStudio.assets.logoLight')}
                    </h3>
                    <p className="text-[11px] text-muted-fg mb-3">
                      {t('admin.brandStudio.assets.logoLightDesc')}
                    </p>
                    <div className="h-16 rounded-lg bg-slate-100 flex items-center justify-center p-2 border border-slate-200">
                      <img
                        src={settings.logoLightUrl}
                        alt="Logo Light"
                        className="max-h-full max-w-full object-contain"
                        onError={(e) => {
                          (e.target as any).style.display = 'none';
                        }}
                      />
                    </div>
                  </div>
                  <div className="pt-2">
                    <input
                      type="file"
                      ref={fileInputRefs['logo-light']}
                      className="hidden"
                      accept="image/png,image/svg+xml,image/webp"
                      onChange={(e) => {
                        if (e.target.files?.[0]) handleFileUpload('logo-light', e.target.files[0]);
                      }}
                    />
                    <button
                      type="button"
                      onClick={() => fileInputRefs['logo-light'].current?.click()}
                      disabled={uploadingAsset === 'logo-light'}
                      className="w-full py-1.5 px-3 text-xs font-medium text-foreground bg-surface border border-surface-border rounded-lg hover:bg-surface-elevated transition-colors flex items-center justify-center gap-1.5"
                    >
                      <Upload className="w-3.5 h-3.5" />
                      {uploadingAsset === 'logo-light'
                        ? t('admin.brandStudio.assets.uploading')
                        : t('admin.brandStudio.assets.uploadBtn')}
                    </button>
                  </div>
                </div>

                {/* Logo Dark */}
                <div className="p-4 bg-surface-elevated border border-surface-border rounded-xl space-y-3 flex flex-col justify-between">
                  <div>
                    <h3 className="text-xs font-bold text-foreground">
                      {t('admin.brandStudio.assets.logoDark')}
                    </h3>
                    <p className="text-[11px] text-muted-fg mb-3">
                      {t('admin.brandStudio.assets.logoDarkDesc')}
                    </p>
                    <div className="h-16 rounded-lg bg-slate-900 flex items-center justify-center p-2 border border-slate-800">
                      <img
                        src={settings.logoDarkUrl}
                        alt="Logo Dark"
                        className="max-h-full max-w-full object-contain"
                        onError={(e) => {
                          (e.target as any).style.display = 'none';
                        }}
                      />
                    </div>
                  </div>
                  <div className="pt-2">
                    <input
                      type="file"
                      ref={fileInputRefs['logo-dark']}
                      className="hidden"
                      accept="image/png,image/svg+xml,image/webp"
                      onChange={(e) => {
                        if (e.target.files?.[0]) handleFileUpload('logo-dark', e.target.files[0]);
                      }}
                    />
                    <button
                      type="button"
                      onClick={() => fileInputRefs['logo-dark'].current?.click()}
                      disabled={uploadingAsset === 'logo-dark'}
                      className="w-full py-1.5 px-3 text-xs font-medium text-foreground bg-surface border border-surface-border rounded-lg hover:bg-surface-elevated transition-colors flex items-center justify-center gap-1.5"
                    >
                      <Upload className="w-3.5 h-3.5" />
                      {uploadingAsset === 'logo-dark'
                        ? t('admin.brandStudio.assets.uploading')
                        : t('admin.brandStudio.assets.uploadBtn')}
                    </button>
                  </div>
                </div>

                {/* Favicon */}
                <div className="p-4 bg-surface-elevated border border-surface-border rounded-xl space-y-3 flex flex-col justify-between">
                  <div>
                    <h3 className="text-xs font-bold text-foreground">
                      {t('admin.brandStudio.assets.favicon')}
                    </h3>
                    <p className="text-[11px] text-muted-fg mb-3">
                      {t('admin.brandStudio.assets.faviconDesc')}
                    </p>
                    <div className="h-14 rounded-lg bg-surface flex items-center justify-center p-2 border border-surface-border">
                      <img
                        src={settings.faviconUrl}
                        alt="Favicon"
                        className="w-8 h-8 object-contain"
                        onError={(e) => {
                          (e.target as any).style.display = 'none';
                        }}
                      />
                    </div>
                  </div>
                  <div className="pt-2">
                    <input
                      type="file"
                      ref={fileInputRefs.favicon}
                      className="hidden"
                      accept="image/x-icon,image/png,image/svg+xml"
                      onChange={(e) => {
                        if (e.target.files?.[0]) handleFileUpload('favicon', e.target.files[0]);
                      }}
                    />
                    <button
                      type="button"
                      onClick={() => fileInputRefs.favicon.current?.click()}
                      disabled={uploadingAsset === 'favicon'}
                      className="w-full py-1.5 px-3 text-xs font-medium text-foreground bg-surface border border-surface-border rounded-lg hover:bg-surface-elevated transition-colors flex items-center justify-center gap-1.5"
                    >
                      <Upload className="w-3.5 h-3.5" />
                      {uploadingAsset === 'favicon'
                        ? t('admin.brandStudio.assets.uploading')
                        : t('admin.brandStudio.assets.uploadBtn')}
                    </button>
                  </div>
                </div>

                {/* OG Social Banner */}
                <div className="p-4 bg-surface-elevated border border-surface-border rounded-xl space-y-3 flex flex-col justify-between">
                  <div>
                    <h3 className="text-xs font-bold text-foreground">
                      {t('admin.brandStudio.assets.ogImage')}
                    </h3>
                    <p className="text-[11px] text-muted-fg mb-3">
                      {t('admin.brandStudio.assets.ogImageDesc')}
                    </p>
                    <div className="h-14 rounded-lg bg-surface flex items-center justify-center p-1 border border-surface-border overflow-hidden">
                      <img
                        src={settings.ogImageUrl}
                        alt="OG Preview"
                        className="w-full h-full object-cover rounded"
                        onError={(e) => {
                          (e.target as any).style.display = 'none';
                        }}
                      />
                    </div>
                  </div>
                  <div className="pt-2">
                    <input
                      type="file"
                      ref={fileInputRefs['og-image']}
                      className="hidden"
                      accept="image/png,image/jpeg,image/webp"
                      onChange={(e) => {
                        if (e.target.files?.[0]) handleFileUpload('og-image', e.target.files[0]);
                      }}
                    />
                    <button
                      type="button"
                      onClick={() => fileInputRefs['og-image'].current?.click()}
                      disabled={uploadingAsset === 'og-image'}
                      className="w-full py-1.5 px-3 text-xs font-medium text-foreground bg-surface border border-surface-border rounded-lg hover:bg-surface-elevated transition-colors flex items-center justify-center gap-1.5"
                    >
                      <Upload className="w-3.5 h-3.5" />
                      {uploadingAsset === 'og-image'
                        ? t('admin.brandStudio.assets.uploading')
                        : t('admin.brandStudio.assets.uploadBtn')}
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* 2. Corporate & Legal Identity */}
            <div className="bg-surface border border-surface-border rounded-2xl p-6 space-y-4">
              <div className="flex items-center gap-2 pb-3 border-b border-surface-border">
                <Building className="w-4 h-4 text-primary" />
                <h2 className="text-sm font-heading font-bold text-foreground">
                  {t('admin.brandStudio.sections.corporateInfo')}
                </h2>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <label className="text-xs font-medium text-foreground">
                    {t('admin.brandStudio.fields.siteName')}
                  </label>
                  <input
                    type="text"
                    value={settings.siteName}
                    onChange={(e) => updateField('siteName', e.target.value)}
                    className="w-full px-3 py-2 text-xs bg-surface-elevated border border-surface-border rounded-xl text-foreground focus:outline-none focus:ring-1 focus:ring-primary"
                  />
                </div>

                <div className="space-y-1.5">
                  <label className="text-xs font-medium text-foreground">
                    {t('admin.brandStudio.fields.slogan')}
                  </label>
                  <input
                    type="text"
                    value={settings.slogan || ''}
                    onChange={(e) => updateField('slogan', e.target.value)}
                    className="w-full px-3 py-2 text-xs bg-surface-elevated border border-surface-border rounded-xl text-foreground focus:outline-none focus:ring-1 focus:ring-primary"
                  />
                </div>

                <div className="sm:col-span-2 space-y-1.5">
                  <label className="text-xs font-medium text-foreground">
                    {t('admin.brandStudio.fields.siteDescription')}
                  </label>
                  <textarea
                    rows={2}
                    value={settings.siteDescription}
                    onChange={(e) => updateField('siteDescription', e.target.value)}
                    className="w-full px-3 py-2 text-xs bg-surface-elevated border border-surface-border rounded-xl text-foreground focus:outline-none focus:ring-1 focus:ring-primary resize-none"
                  />
                </div>

                <div className="space-y-1.5">
                  <label className="text-xs font-medium text-foreground">
                    {t('admin.brandStudio.fields.companyName')}
                  </label>
                  <input
                    type="text"
                    value={settings.companyName}
                    onChange={(e) => updateField('companyName', e.target.value)}
                    className="w-full px-3 py-2 text-xs bg-surface-elevated border border-surface-border rounded-xl text-foreground focus:outline-none focus:ring-1 focus:ring-primary"
                  />
                </div>

                <div className="space-y-1.5">
                  <label className="text-xs font-medium text-foreground">
                    {t('admin.brandStudio.fields.taxId')}
                  </label>
                  <input
                    type="text"
                    value={settings.taxId || ''}
                    onChange={(e) => updateField('taxId', e.target.value)}
                    className="w-full px-3 py-2 text-xs bg-surface-elevated border border-surface-border rounded-xl text-foreground focus:outline-none focus:ring-1 focus:ring-primary"
                  />
                </div>

                <div className="space-y-1.5">
                  <label className="text-xs font-medium text-foreground">
                    {t('admin.brandStudio.fields.contactEmail')}
                  </label>
                  <input
                    type="email"
                    value={settings.contactEmail}
                    onChange={(e) => updateField('contactEmail', e.target.value)}
                    className="w-full px-3 py-2 text-xs bg-surface-elevated border border-surface-border rounded-xl text-foreground focus:outline-none focus:ring-1 focus:ring-primary"
                  />
                </div>

                <div className="space-y-1.5">
                  <label className="text-xs font-medium text-foreground">
                    {t('admin.brandStudio.fields.hotline')}
                  </label>
                  <input
                    type="text"
                    value={settings.hotline || ''}
                    onChange={(e) => updateField('hotline', e.target.value)}
                    className="w-full px-3 py-2 text-xs bg-surface-elevated border border-surface-border rounded-xl text-foreground focus:outline-none focus:ring-1 focus:ring-primary"
                  />
                </div>

                <div className="sm:col-span-2 space-y-1.5">
                  <label className="text-xs font-medium text-foreground">
                    {t('admin.brandStudio.fields.address')}
                  </label>
                  <input
                    type="text"
                    value={settings.address || ''}
                    onChange={(e) => updateField('address', e.target.value)}
                    className="w-full px-3 py-2 text-xs bg-surface-elevated border border-surface-border rounded-xl text-foreground focus:outline-none focus:ring-1 focus:ring-primary"
                  />
                </div>

                <div className="sm:col-span-2 space-y-1.5">
                  <label className="text-xs font-medium text-foreground">
                    {t('admin.brandStudio.fields.copyrightText')}
                  </label>
                  <input
                    type="text"
                    value={settings.copyrightText}
                    onChange={(e) => updateField('copyrightText', e.target.value)}
                    className="w-full px-3 py-2 text-xs bg-surface-elevated border border-surface-border rounded-xl text-foreground focus:outline-none focus:ring-1 focus:ring-primary"
                  />
                </div>
              </div>
            </div>

            {/* 3. Social Media & Public Channels */}
            <div className="bg-surface border border-surface-border rounded-2xl p-6 space-y-4">
              <div className="flex items-center gap-2 pb-3 border-b border-surface-border">
                <Share2 className="w-4 h-4 text-primary" />
                <h2 className="text-sm font-heading font-bold text-foreground">
                  {t('admin.brandStudio.sections.socialChannels')}
                </h2>
              </div>

              <div className="space-y-3">
                <div className="flex items-center gap-2">
                  <Facebook className="w-4 h-4 text-muted-fg shrink-0" />
                  <input
                    type="url"
                    value={settings.socialLinks.facebook || ''}
                    onChange={(e) => updateSocialLink('facebook', e.target.value)}
                    placeholder="https://facebook.com/..."
                    className="w-full px-3 py-2 text-xs bg-surface-elevated border border-surface-border rounded-xl text-foreground focus:outline-none focus:ring-1 focus:ring-primary"
                  />
                </div>

                <div className="flex items-center gap-2">
                  <Twitter className="w-4 h-4 text-muted-fg shrink-0" />
                  <input
                    type="url"
                    value={settings.socialLinks.twitter || ''}
                    onChange={(e) => updateSocialLink('twitter', e.target.value)}
                    placeholder="https://x.com/..."
                    className="w-full px-3 py-2 text-xs bg-surface-elevated border border-surface-border rounded-xl text-foreground focus:outline-none focus:ring-1 focus:ring-primary"
                  />
                </div>

                <div className="flex items-center gap-2">
                  <Linkedin className="w-4 h-4 text-muted-fg shrink-0" />
                  <input
                    type="url"
                    value={settings.socialLinks.linkedin || ''}
                    onChange={(e) => updateSocialLink('linkedin', e.target.value)}
                    placeholder="https://linkedin.com/company/..."
                    className="w-full px-3 py-2 text-xs bg-surface-elevated border border-surface-border rounded-xl text-foreground focus:outline-none focus:ring-1 focus:ring-primary"
                  />
                </div>

                <div className="flex items-center gap-2">
                  <Github className="w-4 h-4 text-muted-fg shrink-0" />
                  <input
                    type="url"
                    value={settings.socialLinks.github || ''}
                    onChange={(e) => updateSocialLink('github', e.target.value)}
                    placeholder="https://github.com/..."
                    className="w-full px-3 py-2 text-xs bg-surface-elevated border border-surface-border rounded-xl text-foreground focus:outline-none focus:ring-1 focus:ring-primary"
                  />
                </div>

                <div className="flex items-center gap-2">
                  <Youtube className="w-4 h-4 text-muted-fg shrink-0" />
                  <input
                    type="url"
                    value={settings.socialLinks.youtube || ''}
                    onChange={(e) => updateSocialLink('youtube', e.target.value)}
                    placeholder="https://youtube.com/@..."
                    className="w-full px-3 py-2 text-xs bg-surface-elevated border border-surface-border rounded-xl text-foreground focus:outline-none focus:ring-1 focus:ring-primary"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Real-Time Live Previews (5 cols) */}
          <div className="lg:col-span-5 space-y-6">
            {/* Google SERP Snippet Preview */}
            <div className="bg-surface border border-surface-border rounded-2xl p-6 space-y-4">
              <div className="flex items-center justify-between pb-3 border-b border-surface-border">
                <div className="flex items-center gap-2">
                  <Globe className="w-4 h-4 text-accent" />
                  <h3 className="text-xs font-heading font-bold text-foreground">
                    {t('admin.brandStudio.preview.googleSerp')}
                  </h3>
                </div>
                <span className="text-[10px] font-mono px-2 py-0.5 rounded-full bg-accent/10 text-accent border border-accent/20">
                  Google SERP
                </span>
              </div>

              {/* Realistic Google Result Card */}
              <div className="p-4 rounded-xl bg-white text-slate-800 border border-slate-200 shadow-sm space-y-1.5 font-sans">
                <div className="flex items-center gap-2">
                  <img
                    src={settings.faviconUrl}
                    alt="Favicon"
                    className="w-4 h-4 rounded-full object-contain"
                    onError={(e) => {
                      (e.target as any).style.display = 'none';
                    }}
                  />
                  <div className="flex flex-col">
                    <span className="text-[12px] font-medium text-slate-900 leading-none">
                      {settings.siteName}
                    </span>
                    <span className="text-[10px] text-slate-500 font-mono">
                      https://{domains.baseDomain}
                    </span>
                  </div>
                </div>

                <h4 className="text-sm font-medium text-[#1a0dab] hover:underline cursor-pointer leading-snug">
                  {settings.siteName} — {settings.slogan || 'Edge AI SaaS'}
                </h4>

                <p className="text-xs text-slate-600 line-clamp-2 leading-relaxed">
                  {settings.siteDescription ||
                    'Empower your creative teams with lightning-fast Edge AI tools.'}
                </p>
              </div>
            </div>

            {/* Website Footer Live Preview */}
            <div className="bg-surface border border-surface-border rounded-2xl p-6 space-y-4">
              <div className="flex items-center justify-between pb-3 border-b border-surface-border">
                <div className="flex items-center gap-2">
                  <Eye className="w-4 h-4 text-primary" />
                  <h3 className="text-xs font-heading font-bold text-foreground">
                    {t('admin.brandStudio.preview.footerPreview')}
                  </h3>
                </div>
                <span className="text-[10px] font-mono px-2 py-0.5 rounded-full bg-primary/10 text-primary border border-primary/20">
                  Platform Footer
                </span>
              </div>

              {/* Realistic Footer Box */}
              <div className="p-5 rounded-xl bg-slate-950 text-slate-200 border border-slate-800 space-y-4 text-xs font-sans">
                {/* Brand & Slogan */}
                <div className="space-y-1.5">
                  <div className="h-6 flex items-center">
                    <img
                      src={settings.logoDarkUrl}
                      alt="Footer Logo"
                      className="max-h-full max-w-[120px] object-contain"
                      onError={(e) => {
                        (e.target as any).style.display = 'none';
                      }}
                    />
                  </div>
                  <p className="text-[11px] text-slate-400 leading-relaxed">
                    {settings.slogan || settings.siteDescription}
                  </p>
                </div>

                {/* Company details */}
                <div className="space-y-1 text-[11px] text-slate-400 pt-2 border-t border-slate-800/80">
                  <div className="flex items-center gap-1.5">
                    <Building className="w-3 h-3 text-slate-500 shrink-0" />
                    <span>{settings.companyName}</span>
                  </div>
                  {settings.address && (
                    <div className="flex items-center gap-1.5">
                      <MapPin className="w-3 h-3 text-slate-500 shrink-0" />
                      <span className="truncate">{settings.address}</span>
                    </div>
                  )}
                  <div className="flex items-center gap-1.5">
                    <Mail className="w-3 h-3 text-slate-500 shrink-0" />
                    <span>{settings.contactEmail}</span>
                  </div>
                  {settings.hotline && (
                    <div className="flex items-center gap-1.5">
                      <Phone className="w-3 h-3 text-slate-500 shrink-0" />
                      <span>{settings.hotline}</span>
                    </div>
                  )}
                </div>

                {/* Social icons */}
                <div className="flex items-center gap-2 pt-2 border-t border-slate-800/80">
                  {settings.socialLinks.facebook && (
                    <a
                      href={settings.socialLinks.facebook}
                      target="_blank"
                      rel="noreferrer"
                      className="p-1 rounded bg-slate-900 text-slate-400 hover:text-white"
                    >
                      <Facebook className="w-3 h-3" />
                    </a>
                  )}
                  {settings.socialLinks.twitter && (
                    <a
                      href={settings.socialLinks.twitter}
                      target="_blank"
                      rel="noreferrer"
                      className="p-1 rounded bg-slate-900 text-slate-400 hover:text-white"
                    >
                      <Twitter className="w-3 h-3" />
                    </a>
                  )}
                  {settings.socialLinks.linkedin && (
                    <a
                      href={settings.socialLinks.linkedin}
                      target="_blank"
                      rel="noreferrer"
                      className="p-1 rounded bg-slate-900 text-slate-400 hover:text-white"
                    >
                      <Linkedin className="w-3 h-3" />
                    </a>
                  )}
                  {settings.socialLinks.github && (
                    <a
                      href={settings.socialLinks.github}
                      target="_blank"
                      rel="noreferrer"
                      className="p-1 rounded bg-slate-900 text-slate-400 hover:text-white"
                    >
                      <Github className="w-3 h-3" />
                    </a>
                  )}
                  {settings.socialLinks.youtube && (
                    <a
                      href={settings.socialLinks.youtube}
                      target="_blank"
                      rel="noreferrer"
                      className="p-1 rounded bg-slate-900 text-slate-400 hover:text-white"
                    >
                      <Youtube className="w-3 h-3" />
                    </a>
                  )}
                </div>

                {/* Copyright */}
                <div className="text-[10px] text-slate-500 pt-1">
                  {settings.copyrightText}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export const BrandSettingsPage = BrandStudioView;
